import React from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

interface PlaceholdersInfoTooltipProps {
  allowedParams: string[];
  /** Prefix for the `data-testid` of the info trigger. */
  testIdPrefix: string;
  /** Translation key for the tooltip heading and the trigger's aria-label. */
  labelKey?: string;
}

/**
 * Info icon listing every supported placeholder with its description in a
 * tooltip. Used next to the message label of the notification cards.
 */
export const PlaceholdersInfoTooltip: React.FC<PlaceholdersInfoTooltipProps> = ({
  allowedParams,
  testIdPrefix,
  labelKey = 'notificationTemplates.placeholdersLabel',
}) => {
  const { t } = useTranslation('admin');

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground"
            aria-label={t(labelKey)}
            data-testid={`${testIdPrefix}-placeholders-info`}
          >
            <Info className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="mb-1 font-medium">{t(labelKey)}</p>
          <ul className="space-y-1">
            {allowedParams.map((param) => (
              <li key={param} className="text-xs">
                <span className="font-mono">{`{${param}}`}</span>
                {' — '}
                {t(`notificationTemplates.placeholders.${param}`, { defaultValue: param })}
              </li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
